import React, { useState, useEffect } from 'react'; 
import { Modal, Form, Input, Select, Button, message, Checkbox, InputNumber } from 'antd';
import { PlusOutlined, CloseOutlined } from '@ant-design/icons';
import axios from 'axios';
import { API_BASE_URL } from '../constants/api';
import CategoryCreateModal from './CategoryCreateModal';

const LAST_CATEGORY_KEY = 'lastMaterialCategoryId';
const { Option } = Select;

const MaterialCreateModal = ({ open, onCancel, onSuccess, form, editingMaterial, keepOpenAfterSave = false }) => {
  const [categories, setCategories] = useState([]);
  const [isCategoryModalVisible, setIsCategoryModalVisible] = useState(false);
  const [categoryForm] = Form.useForm();

  useEffect(() => {
    if (open) {
      form.resetFields();
      fetchCategories();
    }
  }, [open, form]);

  useEffect(() => {
    if (open) {
      if (editingMaterial) {
        form.setFieldsValue({
          name: editingMaterial.name,
          categoryId: editingMaterial.categoryId,
          unitPrice: editingMaterial.unitPrice,
          retailPrice: editingMaterial.retailPrice,
          wholesalePrice: editingMaterial.wholesalePrice,
          allowNegativeBalance: editingMaterial.allowNegativeBalance,
          stockAmount: editingMaterial.stockAmount
        });
      } else if (categories.length > 0) {
        const lastCategoryId = localStorage.getItem(LAST_CATEGORY_KEY);
        if (lastCategoryId) {
          const id = parseInt(lastCategoryId, 10);
          if (!isNaN(id) && categories.some(cat => cat.id === id)) {
            form.setFieldsValue({ categoryId: id });
          }
        }
      }
    }
  }, [open, editingMaterial, categories, form]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}Category/List`);
      if (response.data.code === 0) {
        setCategories(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching categories');
    }
  };

const handleOk = async () => {
    try {
      const values = await form.validateFields();
      
      if (editingMaterial && editingMaterial.id) {
        // Update existing material
        const response = await axios.put(`${API_BASE_URL}Material/Update/${editingMaterial.id}`, values);
        if (response.data.code === 0) {
          message.success('تم تحديث المادة بنجاح');
          form.resetFields(['name', 'unitPrice', 'retailPrice', 'wholesalePrice', 'allowNegativeBalance', 'stockAmount', 'unit']);
          form.scrollToField('name', { block: 'center' });
          if (!keepOpenAfterSave) {
            onSuccess();
          }
        } else {
          message.error(response.data.message || 'فشل في تحديث المادة');
        }
      } else {
        // Create new material
        const response = await axios.post(`${API_BASE_URL}Material/Create`, values);
        if (response.data.code === 0) {
          localStorage.setItem(LAST_CATEGORY_KEY, values.categoryId.toString());
          message.success('تم إنشاء المادة بنجاح');
          form.resetFields(['name', 'unitPrice', 'retailPrice', 'wholesalePrice', 'allowNegativeBalance', 'stockAmount', 'unit']);
          form.scrollToField('name', { block: 'center' });
          if (!keepOpenAfterSave) {
            onSuccess();
          }
        } else {
          message.error(response.data.message || 'فشل في إنشاء المادة');
        }
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'خطأ في حفظ المادة';
      message.error(errorMessage);
    }
  };

  const handleCategoryCreate = () => {
    setIsCategoryModalVisible(true);
  };

  const handleCategoryModalCancel = () => {
    setIsCategoryModalVisible(false);
  };

  const handleCategoryModalSuccess = () => {
    fetchCategories();
    setIsCategoryModalVisible(false);
  };

  return (
    <>
      <Modal
        title={editingMaterial ? 'تعديل المادة' : 'إضافة مادة'}
        open={open}
        closeIcon={<CloseOutlined onClick={onCancel} />}
        footer={[
          !editingMaterial && (
            <Button key="save" type="primary" onClick={handleOk}>
              حفظ وجديد
            </Button>
          ),
          <Button key="saveClose" type="primary" onClick={async () => {
            await handleOk();
            onCancel();
          }}>
            حفظ وإغلاق
          </Button>,
          <Button key="cancel" onClick={onCancel}>
            إلغاء
          </Button>
        ]}
        width="90%"
        style={{ maxWidth: 600 }}
      >
        <Form 
          form={form} 
          layout="vertical"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleOk();
            }
          }}
        >
          <Form.Item
            name="name"
            label="الاسم"
            rules={[{ required: true, message: 'يرجى إدخال اسم المادة!' }]}
          >
            <Input autoFocus />
          </Form.Item>

          <Form.Item
            name="categoryId"
            label="الفئة"
          >
            <Select
              showSearch
              filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
              notFoundContent={<Button icon={<PlusOutlined />} onClick={handleCategoryCreate}>إضافة فئة</Button>}
            >
              {categories.map(category => (
                <Option key={category.id} value={category.id}>
                  {category.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="الأسعار">
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 140 }}>
                <Form.Item name="unitPrice" label="سعر الوحدة" style={{ marginBottom: 8 }}>
                  <InputNumber 
                    style={{ width: '100%' }} 
                    min={0}
                    step={0.01}
                    placeholder="0.00"
                  />
                </Form.Item>
              </div>
              <div style={{ flex: 1, minWidth: 140 }}>
                <Form.Item name="retailPrice" label="سعر البيع" style={{ marginBottom: 8 }}>
                  <InputNumber 
                    style={{ width: '100%' }} 
                    min={0}
                    step={0.01}
                    placeholder="0.00"
                  />
                </Form.Item>
              </div>
              <div style={{ flex: 1, minWidth: 140 }}>
                <Form.Item name="wholesalePrice" label="سعر الجملة" style={{ marginBottom: 8 }}>
                  <InputNumber 
                    style={{ width: '100%' }} 
                    min={0}
                    step={0.01}
                    placeholder="0.00"
                  />
                </Form.Item>
              </div>
            </div>
          </Form.Item>

          <Form.Item
            name="allowNegativeBalance"
            valuePropName="checked"
          >
            <Checkbox>السماح برصيد سالب</Checkbox>
          </Form.Item>

          <Form.Item
            name="stockAmount"
            label="الرصيد الحالي"
          >
            <Input 
              readOnly
              style={{ 
                width: '100%',
                backgroundColor: '#f5f5f5',
                color: '#000',
                borderColor: '#d9d9d9'
              }} 
              placeholder="0.00"
            />
          </Form.Item>

          <Form.Item
            name="unit"
            label="الوحدة"
          >
            <Input placeholder="متر، كيلو، قطعة" maxLength={20} />
          </Form.Item>
        </Form>
      </Modal>
      <CategoryCreateModal
        open={isCategoryModalVisible}
        onCancel={handleCategoryModalCancel}
        onSuccess={handleCategoryModalSuccess}
        form={categoryForm}
        categories={categories}
        editingCategory={null}
      />
    </>
  );
};

export default MaterialCreateModal;
